import React, { useEffect, useRef, useState, useCallback } from "react"
import { connect } from "react-redux"
import "./UniversityList.scss"
import { useNavigate } from "react-router-dom"
import {
  Row,
  Col,
  Button,
  Input,
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import UniversityApi from "Api/UniversityApi"
import LogsApi from "Api/LogsApi"
import { Toast } from "primereact/toast"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { OverlayPanel } from "primereact/overlaypanel"
import CommonTable from "components/Table/CommonTable"
import LogsSidebar from "components/LogsSidebar/LogsSidebar"

const UniversityList = props => {
  document.title = "University "
  const navigate = useNavigate()
  const uploadbtn = useRef()
  const [loading, setLoading] = useState(true)
  const accessToken = localStorage.getItem("accessToken")
  const userData = JSON.parse(localStorage.getItem("userData"))
  const [universityDetails, setUniversityDetails] = useState([])
  const [filteredUniversityDetails, setFilteredUniversityDetails] = useState([])
  const [page, setPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit, setLimit] = useState(10)
  const [filterStatus, setFilterStatus] = useState("all")
  const [selectedUniversities, setSelectedUniversities] = useState([])
  const [excelLoader, setExcelLoader] = useState(false)
  const [addUniversityLoader, setAddUniversityLoader] = useState(false)
  const op = useRef(null)
  const toast = useRef(null)
  const [selectedLogo, setSelectedLogo] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const searchTimeoutRef = useRef(null)
  const [logsVisible, setLogsVisible] = useState(false)
  const [logs, setLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [allLogs, setAllLogs] = useState([])

  const breadcrumbItems = [
    { label: "Pixwik", link: "#" },
    { label: "University", link: "#" },
    { label: "University List", link: "#" },
  ]

  const getAllUniversityDetails = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        limit: limit,
        page: page,
      },
    }

    UniversityApi.getAllUniversity(config)
      .then(response => {
        if (response.data.status) {
          setLoading(false)
          setUniversityDetails(response.data.university)
          setFilteredUniversityDetails(response.data.university) // Initialize filtered data
          setTotalRecords(response.data.totalRecords)
        }
      })
      .catch(error => {})
  }

  const debouncedSearch = useCallback((query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (query.trim() === "") {
        getAllUniversityDetails()
        return
      }

      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          query: query,
          limit: limit,
          page: page,
        },
      }

      try {
        const response = await UniversityApi.searchUniversity(config)
        if (response.data.status) {
          setUniversityDetails(response.data.results[0])
          setFilteredUniversityDetails(response.data.results[0])
          setTotalRecords(response.data.results[0].length)
        }
      } catch (error) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to search universities",
          life: 3000,
        })
      }
    }, 800) // 300ms debounce delay
  }, [accessToken, page, limit])

  const handleSearch = e => {
    const value = e.target.value
    setSearchQuery(value)
    debouncedSearch(value)
  }

  const fetchLogs = async () => {
    setLogsLoading(true)
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
      const response = await LogsApi.getLogsByServiceName("university-service", config)
      const sortedLogs = response.data.logs.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      )
      setAllLogs(sortedLogs)
      setLogs(sortedLogs)
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch logs",
        life: 3000,
      })
    } finally {
      setLogsLoading(false)
    }
  }

  const handleLogsClick = () => {
    setLogsVisible(true)
    fetchLogs()
  }

  const handleFilterChange = (level) => {
    if (level === 'all') {
      setLogs(allLogs);
    } else {
      setLogs(allLogs.filter(log => log.eventType.toLowerCase() === level));
    }
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    getAllUniversityDetails()
  }, [accessToken, page, limit])

  useEffect(() => {
    if (filterStatus === "all") {
      setFilteredUniversityDetails(universityDetails)
    } else {
      const filteredData = universityDetails.filter(
        university => university.verifiedStatus === filterStatus,
      )
      setFilteredUniversityDetails(filteredData)
    }
  }, [filterStatus, universityDetails])

  const totalPages = Math.ceil(totalRecords / limit)

  const handleUploadButtonClick = () => {
    uploadbtn.current.click()
  }

  const handleFileChange = event => {
    setExcelLoader(true)
    const file = event.target.files[0]
    if (!file) return
    const formData = new FormData()
    formData.append("file", file)

    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    UniversityApi.uploadUniversityByExcel(formData, config)
      .then(res => {
        getAllUniversityDetails()
        event.target.value = ""
        setExcelLoader(false)
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: res.data.message,
          life: 3000,
        })
      })
      .catch(err => {
        event.target.value = ""
        setExcelLoader(false)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: err.response.data.message,
          life: 3000,
        })
      })
  }

  const handleAddUniversityClick = () => {
    setAddUniversityLoader(true)
    setTimeout(() => {
      navigate("/addUniversity")
      setAddUniversityLoader(false)
    }, 500)
  }

  const toggleSelectAll = () => {
    if (selectedUniversities.length === filteredUniversityDetails.length) {
      setSelectedUniversities([])
    } else {
      setSelectedUniversities(filteredUniversityDetails.map(uni => uni._id))
    }
  }

  const toggleSelection = uniId => {
    setSelectedUniversities(prevUni =>
      prevUni.includes(uniId)
        ? prevUni.filter(id => id !== uniId)
        : [...prevUni, uniId],
    )
  }

  const handleUpdateStatus = newStatus => {
    if (selectedUniversities.length === 0) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please select at least one university to update.",
        life: 3000,
      })
      return
    }

    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
    const payload = {
      universityIds: selectedUniversities,
      newStatus: newStatus,
    }

    UniversityApi.updateUniversityStatus(payload, config)
      .then(response => {
        if (response.data.status) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: response.data.message,
            life: 3000,
          })
          getAllUniversityDetails()
          setSelectedUniversities([])
        }
      })
      .catch(error => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to update university status.",
          life: 3000,
        })
      })
  }

  const toggleLogoModal = (logoUrl = null) => {
    setSelectedLogo(logoUrl)
    setIsModalOpen(!isModalOpen)
  }

  return (
    <>
      <Toast ref={toast} />
      <LogsSidebar 
        visible={logsVisible}
        onHide={() => setLogsVisible(false)}
        logs={logs}
        loading={logsLoading}
        onRefresh={fetchLogs}
        onFilterChange={handleFilterChange}
        allLogs={allLogs}
      />
      <Modal
        isOpen={isModalOpen}
        toggle={() => toggleLogoModal()}
        centered
        size="md"
      >
        <ModalHeader toggle={() => toggleLogoModal()}>
          University Logo
        </ModalHeader>
        <ModalBody className="text-center">
          {selectedLogo && (
            <img
              src={selectedLogo}
              alt="University Logo"
              style={{ maxWidth: "100%" }}
            />
          )}
        </ModalBody>
      </Modal>
      <Row>
        <Col xs="12">
          <div className="university-list-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb data={breadcrumbItems} pageTitle="University" />

            <div className="d-flex align-items-center gap-3">
              {selectedUniversities.length === 0 && (
                <>
                  <Input
                    type="text"
                    placeholder="Search University"
                    className="form-control"
                    style={{ width: "300px" }}
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                  <input
                    type="file"
                    ref={uploadbtn}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <Button
                    type="button"
                    color="success"
                    className="waves-effect"
                    style={{ minWidth: "40px" }}
                    onClick={handleUploadButtonClick}
                  >
                    {excelLoader ? (
                      <Spinner size="sm" />
                    ) : (
                      <i className="mdi mdi-microsoft-excel" />
                    )}
                  </Button>

                  <Button
                    type="button"
                    color="secondary"
                    className="waves-effect"
                    style={{ minWidth: "120px" }}
                    onClick={handleAddUniversityClick}
                  >
                    Add University
                  </Button>

                  <Button
                    type="button"
                    color="info"
                    className="waves-effect"
                    style={{ minWidth: "100px" }}
                    onClick={handleLogsClick}
                  >
                    <i className="mdi mdi-file-document-outline me-1" />
                    Logs
                  </Button>
                </>
              )}

              {selectedUniversities.length > 0 && (
                <>
                  <Button
                    type="button"
                    color="primary"
                    className="waves-effect"
                    style={{ minWidth: "100px" }}
                    onClick={() => handleUpdateStatus("approve")}
                  >
                    Approve
                  </Button>
                  <Button
                    type="button"
                    color="danger"
                    className="waves-effect"
                    style={{ minWidth: "100px" }}
                    onClick={() => handleUpdateStatus("reject")}
                  >
                    Reject
                  </Button>
                </>
              )}
            </div>
          </div>

          <CommonTable
            loading={loading}
            data={filteredUniversityDetails}
            page={page}
            totalPages={totalPages}
            limit={limit}
            setPage={setPage}
            selectable={true}
            selectedItems={selectedUniversities}
            onSelectItem={toggleSelection}
            onSelectAll={toggleSelectAll}
            columns={[
              { header: "#", render: (_, i) => (page - 1) * limit + i + 1 },
              {
                header: "Logo",
                render: uni =>
                  uni.logo ? (
                    <img
                      src={uni.logo.url}
                      alt={uni.logo.alt}
                      style={{ width: 40, height: 40, borderRadius: "50%" }}
                    />
                  ) : (
                    "N/A"
                  ),
              },
              { header: "University Name", key: "universityName" },
              {
                header: "Type",
                render: uni =>
                  uni.type?.charAt(0).toUpperCase() + uni.type?.slice(1),
              },
              { header: "Year Established", key: "yearEstablished" },
              {
                header: "UGC Approval",
                render: uni => (uni.ugcApproval ? "Yes" : "No"),
              },
              {
                header: (
                  <>
                    <span className="d-flex align-items-center gap-2 ">
                      <span>Verified Status</span>
                      <span
                        onClick={e => op.current.toggle(e)}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="mdi mdi-filter" />
                      </span>
                    </span>
                    <OverlayPanel ref={op}>
                      <Input
                        type="select"
                        value={filterStatus}
                        onChange={e => {
                          setFilterStatus(e.target.value)
                          op.current.hide()
                        }}
                        style={{ width: "150px" }}
                      >
                        <option value="all">All</option>
                        <option value="approve">Approved</option>
                        <option value="reject">Rejected</option>
                        <option value="pending">Pending</option>
                      </Input>
                    </OverlayPanel>
                  </>
                ),
                render: uni => (
                  <span
                    className={`status ${uni.verifiedStatus.toLowerCase()}`}
                  >
                    {uni.verifiedStatus === "approve"
                      ? "Approved"
                      : uni.verifiedStatus === "reject"
                        ? "Rejected"
                        : "Pending"}
                  </span>
                ),
              },
              {
                header: "Edit",
                render: uni => (
                  <span
                    className="edit-university"
                    onClick={() =>
                      navigate(`/edit-university/${uni.universityCode}`, {
                        state: uni,
                      })
                    }
                  >
                    <i className="ion ion-md-create" />
                  </span>
                ),
              },
              {
                header: "View",
                render: uni => (
                  <span
                    onClick={() => {
                      navigate(`/view-university/${uni?.universityCode}`)
                    }}
                    className="view-university"
                  >
                    <i className="ion ion-md-eye" />
                  </span>
                ),
              },
            ]}
          />
        </Col>
      </Row>
    </>
  )
}

export default UniversityList
